import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveRentalCard = ({ rental }) => {
  const navigate = useNavigate();
  const [isWithinDropoffHour, setIsWithinDropoffHour] = useState(false);
  const [minutesUntilDropoff, setMinutesUntilDropoff] = useState(null);

  const getDaysUntil = (dateString) => {
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Build a full Date object from returnDate + dropoffTime (e.g. "14:00" or "2:00 PM")
  const getDropoffDateTime = () => {
    if (!rental?.returnDate) return null;

    const baseDate = new Date(rental.returnDate);
    // Normalize to midnight so we can set time
    const dropoffDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());

    if (rental?.dropoffTime) {
      // Support "HH:MM" (24h) or "H:MM AM/PM" formats
      const timeStr = rental?.dropoffTime?.trim();
      const ampmMatch = timeStr?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      const militaryMatch = timeStr?.match(/^(\d{1,2}):(\d{2})$/);

      if (ampmMatch) {
        let hours = parseInt(ampmMatch?.[1], 10);
        const minutes = parseInt(ampmMatch?.[2], 10);
        const period = ampmMatch?.[3]?.toUpperCase();
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        dropoffDate?.setHours(hours, minutes, 0, 0);
      } else if (militaryMatch) {
        dropoffDate?.setHours(parseInt(militaryMatch?.[1], 10), parseInt(militaryMatch?.[2], 10), 0, 0);
      } else {
        // Fallback: end of return day
        dropoffDate?.setHours(23, 59, 0, 0);
      }
    } else {
      // No specific time set — treat end of return day as dropoff
      dropoffDate?.setHours(23, 59, 0, 0);
    }

    return dropoffDate;
  };

  useEffect(() => {
    const checkDropoffTime = () => {
      const dropoffDateTime = getDropoffDateTime();
      if (!dropoffDateTime) return;

      const now = new Date();
      const diffMs = dropoffDateTime - now;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      // Show banner if within 60 minutes before dropoff (and not past it by more than 30 min)
      if (diffMinutes >= -30 && diffMinutes <= 60) {
        setIsWithinDropoffHour(true);
        setMinutesUntilDropoff(diffMinutes);
      } else {
        setIsWithinDropoffHour(false);
        setMinutesUntilDropoff(null);
      }
    };

    checkDropoffTime();
    // Re-check every minute
    const interval = setInterval(checkDropoffTime, 60 * 1000);
    return () => clearInterval(interval);
  }, [rental?.returnDate, rental?.dropoffTime]);

  const daysUntilPickup = getDaysUntil(rental?.pickupDate);
  const isUpcoming = daysUntilPickup > 0;
  const daysUntilReturn = getDaysUntil(rental?.returnDate);
  const isReturnDue = daysUntilReturn <= 1 && daysUntilReturn >= -1;
  const isConfirmed = rental?.status === 'confirmed';

  const handleStartCheckIn = () => {
    const params = new URLSearchParams({
      bookingId: rental?.id || '',
      ownerId: rental?.ownerId || '',
      trailerListingId: rental?.trailerListingId || rental?.trailerId || ''
    });
    navigate(`/renter-check-in-process?${params?.toString()}`);
  };

  const handleStartReturnInspection = () => {
    const params = new URLSearchParams({
      bookingId: rental?.id || '',
      ownerId: rental?.ownerId || '',
      trailerListingId: rental?.trailerListingId || rental?.trailerId || ''
    });
    navigate(`/trailer-return-inspection?${params?.toString()}`);
  };

  const getDropoffCountdownLabel = () => {
    if (minutesUntilDropoff === null) return '';
    if (minutesUntilDropoff < 0) return 'Drop-off time has passed — complete your return now';
    if (minutesUntilDropoff === 0) return 'Drop-off time is now — complete your return';
    if (minutesUntilDropoff < 60) return `Drop-off in ${minutesUntilDropoff} minute${minutesUntilDropoff === 1 ? '' : 's'} — start your return process`;
    return 'Drop-off in 1 hour — start your return process';
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-smooth">
      {/* 1-Hour Pre-Dropoff Checkout Banner */}
      {isWithinDropoffHour && (
        <div className="bg-[#FF6B35] px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="AlertTriangle" size={18} className="text-white flex-shrink-0" strokeWidth={2.5} />
            <p className="font-body text-sm font-semibold text-white leading-tight">
              {getDropoffCountdownLabel()}
            </p>
          </div>
          <button
            onClick={handleStartReturnInspection}
            className="flex-shrink-0 bg-white text-[#FF6B35] text-xs font-bold px-3 py-1.5 rounded-md hover:bg-orange-50 transition-colors whitespace-nowrap"
          >
            Start Return
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-48 h-48 sm:h-auto overflow-hidden flex-shrink-0">
          <Image
            src={rental?.trailerImage}
            alt={rental?.trailerImageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-1">
                {rental?.trailerName}
              </h3>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="MapPin" size={14} strokeWidth={2} />
                <span className="font-body">{rental?.location}</span>
              </div>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                rental?.status === 'confirmed' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
              }`}
            >
              {rental?.status === 'confirmed' ? 'Confirmed' : 'Upcoming'}
            </span>
          </div>

          {isConfirmed && (
            <div className="bg-primary/10 border border-primary/20 rounded-md p-3 mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="ClipboardCheck" size={16} className="text-primary" strokeWidth={2} />
                <p className="font-body text-sm text-primary font-medium">
                  Complete your pre-rental check-in before pickup
                </p>
              </div>
            </div>
          )}

          {isUpcoming && daysUntilPickup <= 7 && !isConfirmed && (
            <div className="bg-warning/10 border border-warning/20 rounded-md p-3 mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-warning" strokeWidth={2} />
                <p className="font-body text-sm text-warning font-medium">
                  Pickup in {daysUntilPickup} {daysUntilPickup === 1 ? 'day' : 'days'}
                </p>
              </div>
            </div>
          )}

          {isReturnDue && !isWithinDropoffHour && (
            <div className="bg-accent/10 border border-accent/20 rounded-md p-3 mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Camera" size={16} className="text-accent" strokeWidth={2} />
                <p className="font-body text-sm text-accent font-medium">
                  Return inspection required — Document trailer condition before returning
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p className="font-caption text-xs text-muted-foreground mb-1">Pickup Date</p>
              <p className="font-body text-sm font-medium text-foreground">
                {formatDate(rental?.pickupDate)}
                {rental?.pickupTime && (
                  <span className="text-muted-foreground ml-1">@ {rental?.pickupTime}</span>
                )}
              </p>
            </div>
            <div>
              <p className="font-caption text-xs text-muted-foreground mb-1">Return Date</p>
              <p className="font-body text-sm font-medium text-foreground">
                {formatDate(rental?.returnDate)}
                {rental?.dropoffTime && (
                  <span className="text-muted-foreground ml-1">@ {rental?.dropoffTime}</span>
                )}
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-3 mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-caption text-xs text-muted-foreground mb-1">Owner</p>
                <p className="font-body text-sm font-medium text-foreground">{rental?.ownerName}</p>
              </div>
              <div className="text-right">
                <p className="font-caption text-xs text-muted-foreground mb-1">Total Cost</p>
                <p className="font-heading text-lg font-bold text-primary">${rental?.totalCost}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            {isConfirmed && (
              <Button
                variant="default"
                size="sm"
                iconName="ClipboardCheck"
                onClick={handleStartCheckIn}
                className="flex-1"
              >
                Start Check-In
              </Button>
            )}
            {isReturnDue && (
              <Button
                variant="default"
                size="sm"
                iconName="Camera"
                onClick={handleStartReturnInspection}
                className="flex-1"
              >
                Return Inspection
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              iconName="MessageSquare"
              onClick={() => navigate('/messaging-center')}
              className="flex-1"
            >
              Message Owner
            </Button>
            {rental?.ownerPhone && (
              <Button
                variant="outline"
                size="sm"
                iconName="Phone"
                onClick={() => window.open(`tel:${rental?.ownerPhone}`, '_self')}
                className="flex-1"
              >
                Call Owner
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              iconName="AlertCircle"
              onClick={() => navigate(`/charge-dispute?bookingId=${rental?.id}`)}
              className="flex-1"
            >
              File Dispute
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="MapPin"
              onClick={() => navigate(`/trailer-detail/${rental?.trailerId || rental?.id}`)}
              className="flex-1"
            >
              View Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveRentalCard;